// ============================================================================
import { apiClient } from '../../utils/apiHelper';
// 🔒 LOCKED CODE - DO NOT MODIFY WITHOUT AUTHORIZATION
// Lock Date: December 8, 2025
// Documentation: /app/CODE_LOCKED_DO_NOT_MODIFY.md
//
// CRITICAL POINTS:
// 1. NO SEMESTER column or filter (removed per requirements)
// 2. Uses actual data from backend API (not assumptions)
// 3. All filters working: Course, Session, Date Range, Student
// 4. Field names must match backend: amount_paid, course_name, etc.
//
// For semester/installment tracking: Use Fee Installment Report
// For aggregate overview: Use this Due Fees Report
// ============================================================================

import { filterSessionsByCourse } from '../../utils/sessionUtils';
import React, { useState, useEffect } from 'react';
import { Search, Download, Filter, X, Calendar, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { useReportPagination } from '@/components/ReportPagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// SECURITY FIX: Read token at request time (not module load) to avoid stale
// token bleeding across tenant logins which broke tenant isolation.
const getToken = () => localStorage.getItem('token');

const DueFeesReport = () => {
  const [dueFeesData, setDueFeesData] = useState({ summary: {}, students: [] });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    studentId: '',
    session: '',
    course: ''
  });

  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [students, setStudents] = useState([]);

  

  useEffect(() => {
    fetchMasterData();
    fetchDueFeesReport();
  }, []);

  const fetchMasterData = async () => {
    try {
      const [coursesRes, sessionsRes, studentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/master-settings/courses`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        }),
        axios.get(`${API_URL}/api/master-settings/sessions`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        }),
        axios.get(`${API_URL}/api/students/dropdown`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        })
      ]);
      
      setCourses(coursesRes.data);
      setSessions(sessionsRes.data);
      setStudents(studentsRes.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchDueFeesReport = async (customFilters = null) => {
    setLoading(true);
    try {
      // Use custom filters if provided, otherwise use state filters
      const activeFilters = customFilters || filters;
      
      const params = new URLSearchParams();
      if (activeFilters.fromDate) params.append('from_date', activeFilters.fromDate);
      if (activeFilters.toDate) params.append('to_date', activeFilters.toDate);
      if (activeFilters.studentId) params.append('student_id', activeFilters.studentId);
      if (activeFilters.session) params.append('session', activeFilters.session);
      if (activeFilters.course) params.append('course', activeFilters.course);

      const response = await axios.get(
        `${API_URL}/api/fees/due-fees-report?${params.toString()}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      setDueFeesData(response.data);
    } catch (error) {
      console.error('Error fetching due fees report:', error);
      alert('Failed to fetch due fees report');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    console.log('Applying filters:', filters);
    // Pass current filters explicitly to avoid stale state
    fetchDueFeesReport(filters);
    setShowFilters(false);
  };

  const clearFilters = () => {
    const emptyFilters = {
      fromDate: '',
      toDate: '',
      studentId: '',
      session: '',
      course: ''
    };
    setFilters(emptyFilters);
    // Pass empty filters explicitly
    fetchDueFeesReport(emptyFilters);
  };

  const exportToCSV = () => {
    const headers = [
      'Admission No',
      'Student Name',
      'Course',
      'Session',
      'Total Fee',
      'Booking',
      'Admission',
      'Discount',
      'Scholarship',
      'Installment Paid',
      'Total Paid',
      'Overdue',
      'Fine',
      'Total Due',
      'Phone',
      'Email'
    ];

    const csvData = dueFeesData.students.map(student => [
      student.admission_number,
      student.student_name,
      student.course,
      student.session,
      student.total_fee,
      student.booking_fee || 0,
      student.admission_fee || 0,
      student.discount || 0,
      student.scholarship || 0,
      student.installment_paid || 0,
      student.total_paid,
      student.overdue_amount,
      student.fine_amount,
      (student.total_due || 0) + (student.fine_amount || 0),
      student.phone,
      student.email
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `due_fees_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const filteredStudents = dueFeesData.students.filter(student =>
    student.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.admission_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.course.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const { paginatedData: pageStudents, PaginationControls } = useReportPagination(filteredStudents);

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Due Fees Report</h1>
          <p className="text-gray-600 mt-1">Comprehensive due fees report with overdue and fine details</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowFilters(!showFilters)}
            className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
          >
            <Filter size={18} />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-white text-blue-600 rounded-full px-2 py-0.5 text-xs font-bold">
                {activeFilterCount}
              </span>
            )}
          </Button>
          <Button
            onClick={exportToCSV}
            className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
            disabled={dueFeesData.students.length === 0}
          >
            <Download size={18} />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Filter Options</h3>
            <button onClick={() => setShowFilters(false)} className="text-gray-500 hover:text-gray-700">
              <X size={20} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                value={filters.fromDate}
                onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                value={filters.toDate}
                onChange={(e) => handleFilterChange('toDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Student</label>
              <select
                value={filters.studentId}
                onChange={(e) => handleFilterChange('studentId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Students</option>
                {students.map(student => (
                  <option key={student.id} value={student.id}>
                    {student.full_name} ({student.admission_number})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Course</label>
              <select
                value={filters.course}
                onChange={(e) => handleFilterChange('course', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Courses</option>
                {courses.map(course => (
                  <option key={course.id} value={course.name}>
                    {course.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Session</label>
              <select
                value={filters.session}
                onChange={(e) => handleFilterChange('session', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Sessions</option>
                {filterSessionsByCourse(sessions, courses.find(c => c.name === filters.course)?.id).map(session => (
                  <option key={session.id} value={session.name}>
                    {session.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button
              onClick={clearFilters}
              variant="outline"
              className="flex items-center gap-2"
            >
              Clear All
            </Button>
            <Button
              onClick={applyFilters}
              className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-600 text-sm font-medium">Total Students</p>
              <p className="text-3xl font-bold text-blue-900 mt-2">
                {dueFeesData.summary.total_students || 0}
              </p>
            </div>
            <AlertTriangle className="h-12 w-12 text-blue-500 opacity-50" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-xl border border-orange-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-600 text-sm font-medium">Total Due</p>
              <p className="text-3xl font-bold text-orange-900 mt-2">
                ₹{(dueFeesData.summary.total_due_amount || 0).toLocaleString('en-IN')}
              </p>
            </div>
            <Calendar className="h-12 w-12 text-orange-500 opacity-50" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-xl border border-red-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-600 text-sm font-medium">Overdue Amount</p>
              <p className="text-3xl font-bold text-red-900 mt-2">
                ₹{(dueFeesData.summary.total_overdue_amount || 0).toLocaleString('en-IN')}
              </p>
            </div>
            <AlertTriangle className="h-12 w-12 text-red-500 opacity-50" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-600 text-sm font-medium">Total Fine</p>
              <p className="text-3xl font-bold text-purple-900 mt-2">
                ₹{(dueFeesData.summary.total_fine_amount || 0).toLocaleString('en-IN')}
              </p>
            </div>
            <AlertTriangle className="h-12 w-12 text-purple-500 opacity-50" />
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-3 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search by student name, admission number, or course..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adm No</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Fee</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Booking</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Admission</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Discount</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Scholar</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Inst Paid</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Paid</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Overdue</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Fine</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Due</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="13" className="px-6 py-8 text-center text-gray-500">
                    Loading due fees report...
                  </td>
                </tr>
              ) : filteredStudents.length === 0 ? (
                <tr>
                  <td colSpan="13" className="px-6 py-8 text-center text-gray-500">
                    No due fees found
                  </td>
                </tr>
              ) : (
                pageStudents.map((student) => (
                  <tr key={student.student_id} className="hover:bg-gray-50">
                    <td className="px-3 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                      {student.admission_number}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-900">{student.student_name}</td>
                    <td className="px-3 py-3 text-sm text-gray-500">{student.course}</td>
                    <td className="px-3 py-3 text-sm text-right text-gray-900 font-medium whitespace-nowrap" title="Gross Course Fee">
                      ₹{(student.total_fee || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-3 py-3 text-sm text-right text-blue-600 whitespace-nowrap">
                      ₹{(student.booking_fee || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-3 py-3 text-sm text-right text-blue-600 whitespace-nowrap">
                      ₹{(student.admission_fee || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-3 py-3 text-sm text-right text-amber-600 whitespace-nowrap">
                      ₹{(student.discount || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-3 py-3 text-sm text-right text-amber-600 whitespace-nowrap">
                      ₹{(student.scholarship || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-3 py-3 text-sm text-right text-green-600 whitespace-nowrap">
                      ₹{(student.installment_paid || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-3 py-3 text-sm text-right text-green-700 font-semibold whitespace-nowrap" title="Admission + Booking + Installments paid">
                      ₹{(student.total_paid || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-3 py-3 text-sm text-right text-red-600 font-medium whitespace-nowrap">
                      ₹{(student.overdue_amount || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-3 py-3 text-sm text-right text-purple-600 font-medium whitespace-nowrap">
                      ₹{(student.fine_amount || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-3 py-3 text-sm text-right text-orange-700 font-bold whitespace-nowrap" title="Total Due = Total Fee − Booking − Admission − Discount − Scholarship − Installment Paid + Fine">
                      ₹{((student.total_due || 0) + (student.fine_amount || 0)).toLocaleString('en-IN')}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <PaginationControls />
      </div>

      {/* Footer Summary */}
      {!loading && filteredStudents.length > 0 && (
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <p className="text-sm text-gray-600">Avg Due/Student</p>
              <p className="text-xl font-bold text-gray-900">
                ₹{(dueFeesData.summary.average_due_per_student || 0).toLocaleString('en-IN')}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Due</p>
              <p className="text-xl font-bold text-orange-600">
                ₹{(dueFeesData.summary.total_due_amount || 0).toLocaleString('en-IN')}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Overdue</p>
              <p className="text-xl font-bold text-red-600">
                ₹{(dueFeesData.summary.total_overdue_amount || 0).toLocaleString('en-IN')}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Fine</p>
              <p className="text-xl font-bold text-purple-600">
                ₹{(dueFeesData.summary.total_fine_amount || 0).toLocaleString('en-IN')}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Grand Total Payable</p>
              <p className="text-xl font-bold text-gray-900">
                ₹{(dueFeesData.summary.total_payable || 0).toLocaleString('en-IN')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DueFeesReport;
